import {
  isTask,
  MESSAGE_TYPE_API,
  MESSAGE_TYPE_FINAL_ANSWER,
  MESSAGE_TYPE_FINAL_ANSWER_STREAM,
  MESSAGE_TYPE_INTERMEDIATE_ANSWER,
  MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM,
  MESSAGE_TYPE_LLM_ERROR,
  MESSAGE_TYPE_TOOL_CALL,
  MESSAGE_TYPE_TOOL_ERROR,
  MESSAGE_TYPE_TOOL_MESSAGE,
  MessageType,
  TaskMessage,
} from '@constants/agentTypes'
import {
  InternalMessage,
  QueuedMessageUpdate,
  SubTeammateStructuredOutput,
  ToolCallApproval,
  WebsocketMessageData,
} from '@slices/userMessage'
import { Dispatch } from 'react'

export type InferenceMessageState = {
  messages: InternalMessage[]
  tasks: TaskMessage[]
}

export interface InferenceMessageActions {
  resetMessages: () => void
  addMessage: (newMessage: InternalMessage) => void
  setMessages: (messages: InternalMessage[]) => void
  addStreamMessage: (newMessage: InternalMessage) => void
  updateStreamMessage: (messageUpdate: { token: string; data?: WebsocketMessageData }) => void
  finalizeTemporaryMessage: (types: MessageType[], id?: string) => void
  updateQueuedMessage: (update: QueuedMessageUpdate) => void
  updateTaskStatus: (updatedTask: TaskMessage) => void
  markToolCallPendingApproval: (messageUpdate: Pick<InternalMessage, 'id' | 'data'>) => void
  linkSubTeammateChildSession: (parentToolCallMessageId: string, childSessionId: string, childChatflowName?: string) => void
  setSubTeammateAcceptanceState: (update: {
    parentToolCallMessageId: string
    pendingUserAcceptance: boolean
    structuredOutput: SubTeammateStructuredOutput
  }) => void
  clearSubTeammateAcceptanceState: (parentToolCallMessageId: string) => void
  markSubTeammateRerunning: (parentToolCallMessageId: string) => void
}

type InferenceMessageAction =
  | { type: 'resetMessages' }
  | { type: 'addMessage'; message: InternalMessage }
  | { type: 'setMessages'; messages: InternalMessage[] }
  | { type: 'addStreamMessage'; message: InternalMessage }
  | { type: 'updateStreamMessage'; token: string; data?: WebsocketMessageData }
  | { type: 'finalizeTemporaryMessage'; types: MessageType[]; id?: string }
  | { type: 'updateQueuedMessage'; update: QueuedMessageUpdate }
  | { type: 'updateTaskStatus'; task: TaskMessage }
  | { type: 'markToolCallPendingApproval'; messageUpdate: Pick<InternalMessage, 'id' | 'data'> }
  | { type: 'linkSubTeammateChildSession'; parentToolCallMessageId: string; childSessionId: string; childChatflowName?: string }
  | {
      type: 'setSubTeammateAcceptanceState'
      parentToolCallMessageId: string
      pendingUserAcceptance: boolean
      structuredOutput: SubTeammateStructuredOutput
    }
  | { type: 'clearSubTeammateAcceptanceState'; parentToolCallMessageId: string }
  | { type: 'markSubTeammateRerunning'; parentToolCallMessageId: string }

export const initialInferenceMessageState: InferenceMessageState = {
  messages: [],
  tasks: [],
}

const isExistingTask = message => isTask(message)

const tagBridgedChildMessage = (state: InferenceMessageState, newMessage: InternalMessage): InternalMessage => {
  const childSessionId = newMessage.data?.childSessionId ?? (newMessage as InternalMessage & { childSessionId?: string }).childSessionId
  if (!childSessionId || newMessage.parentToolCallId) {
    return newMessage
  }

  const parent = state.messages.find(
    message => message.toolPlan?.name === 'sub_teammate' && message.subTeammateChildSessionId === childSessionId && message.id
  )
  if (!parent?.id) {
    return newMessage
  }

  return { ...newMessage, parentToolCallId: parent.id }
}

const insertRequestMessage = (messages: InternalMessage[], message: InternalMessage): InternalMessage[] => {
  if (!message.requestMessageId) return [...messages, message]

  const requestIndex = messages.findIndex(candidate => candidate.type === 'userInput' && candidate.id === message.requestMessageId)
  if (requestIndex === -1) return [...messages, message]

  const nextRequestIndex = messages.findIndex((candidate, index) => index > requestIndex && candidate.type === 'userInput')
  const insertionIndex = nextRequestIndex === -1 ? messages.length : nextRequestIndex

  return [...messages.slice(0, insertionIndex), message, ...messages.slice(insertionIndex)]
}

const groupRequestMessages = (messages: InternalMessage[], requestMessageId: string): InternalMessage[] => {
  const requestIndex = messages.findIndex(message => message.type === 'userInput' && message.id === requestMessageId)
  if (requestIndex === -1) return messages

  const correlatedMessages = messages.filter((message, index) => index !== requestIndex && message.requestMessageId === requestMessageId)
  if (correlatedMessages.length === 0) return messages

  const remainingMessages = messages.filter((message, index) => index === requestIndex || message.requestMessageId !== requestMessageId)
  const remainingRequestIndex = remainingMessages.findIndex(message => message.type === 'userInput' && message.id === requestMessageId)
  const nextRequestIndex = remainingMessages.findIndex((message, index) => index > remainingRequestIndex && message.type === 'userInput')
  const insertionIndex = nextRequestIndex === -1 ? remainingMessages.length : nextRequestIndex

  return [...remainingMessages.slice(0, insertionIndex), ...correlatedMessages, ...remainingMessages.slice(insertionIndex)]
}

const appendMessageInternal = (state: InferenceMessageState, incomingMessage: InternalMessage): InferenceMessageState => {
  let newMessage = tagBridgedChildMessage(state, incomingMessage)
  const currentLastMessage = state.messages.slice(-1)[0]
  if (currentLastMessage?.type === MESSAGE_TYPE_LLM_ERROR && newMessage.type === MESSAGE_TYPE_API) {
    return state
  }

  let result: InferenceMessageState = {
    ...state,
    tasks: isTask(newMessage) ? [...state.tasks.filter(task => task.value !== newMessage.value), newMessage] : state.tasks,
  }

  if (newMessage.type === 'userInput' && newMessage.id) {
    const existingUserInputIndex = result.messages.findIndex(message => message.type === 'userInput' && message.id === newMessage.id)
    if (existingUserInputIndex !== -1) {
      return {
        ...result,
        messages: result.messages.map((message, index) => (index === existingUserInputIndex ? { ...message, ...newMessage } : message)),
      }
    }

    const oldestUserInputMessageIndex = result.messages.findIndex(message => message.type === 'userInput' && !message.id)
    if (oldestUserInputMessageIndex !== -1) {
      result = {
        ...result,
        messages: result.messages.map((message, index) =>
          index === oldestUserInputMessageIndex ? { ...message, id: newMessage.id } : message
        ),
      }
      return result
    }
  }

  if (newMessage.type === MESSAGE_TYPE_FINAL_ANSWER) {
    const existingFinalMessageIdx = newMessage.id
      ? result.messages.findIndex(message => message.type === MESSAGE_TYPE_FINAL_ANSWER && message.id === newMessage.id)
      : -1
    const existingStreamingMessageIdx = result.messages.findLastIndex(
      message =>
        message.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM &&
        ((newMessage.requestMessageId && message.requestMessageId === newMessage.requestMessageId) ||
          (newMessage.data?.parentRunId && message.data?.parentRunId === newMessage.data.parentRunId) ||
          message.value === newMessage.value)
    )
    if (existingStreamingMessageIdx > -1) {
      const reconciledMessages = result.messages.flatMap((message, index) => {
        if (index === existingFinalMessageIdx && index !== existingStreamingMessageIdx) return []
        if (index !== existingStreamingMessageIdx) return [message]
        return [
          {
            ...message,
            id: newMessage.id,
            type: MESSAGE_TYPE_FINAL_ANSWER,
            value: newMessage.value,
            requestMessageId: newMessage.requestMessageId ?? message.requestMessageId,
          },
        ]
      })
      return {
        ...result,
        messages: newMessage.requestMessageId ? groupRequestMessages(reconciledMessages, newMessage.requestMessageId) : reconciledMessages,
      }
    }

    if (existingFinalMessageIdx > -1) {
      return {
        ...result,
        messages: result.messages.map((message, index) => (index === existingFinalMessageIdx ? { ...message, ...newMessage } : message)),
      }
    }
  }

  if (newMessage.type === MESSAGE_TYPE_INTERMEDIATE_ANSWER) {
    const existingStreamingMessageIdx = result.messages.findLastIndex(
      message =>
        message.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM &&
        ((newMessage.requestMessageId && message.requestMessageId === newMessage.requestMessageId) ||
          (newMessage.data?.parentRunId && message.data?.parentRunId === newMessage.data.parentRunId) ||
          message.value === newMessage.value)
    )
    if (existingStreamingMessageIdx > -1) {
      const reconciledMessages = result.messages.map((message, index) =>
        index === existingStreamingMessageIdx
          ? {
              ...message,
              type: MESSAGE_TYPE_INTERMEDIATE_ANSWER,
              id: newMessage.id,
              requestMessageId: newMessage.requestMessageId ?? message.requestMessageId,
            }
          : message
      )
      return {
        ...result,
        messages: newMessage.requestMessageId ? groupRequestMessages(reconciledMessages, newMessage.requestMessageId) : reconciledMessages,
      }
    }
  }

  if (newMessage.type === MESSAGE_TYPE_TOOL_CALL) {
    const toolPlan = {
      name: newMessage.data?.tool?.name ?? newMessage.data?.tool?.id ?? 'unknown',
      label: newMessage.data?.tool?.label ?? newMessage.data?.tool?.name ?? newMessage.data?.tool?.id ?? 'unknown',
      input: newMessage.value,
    }
    result = {
      ...result,
      messages: insertRequestMessage(result.messages, {
        ...newMessage,
        toolPlan,
        toolCallMessageId: newMessage.id,
      }),
    }
    newMessage = {
      ...newMessage,
      value: undefined,
      type: MESSAGE_TYPE_TOOL_MESSAGE,
      status: 'started',
      toolPlan,
      toolCallMessageId: newMessage.id,
    }
  } else if (newMessage.type === MESSAGE_TYPE_TOOL_MESSAGE || newMessage.type === MESSAGE_TYPE_TOOL_ERROR) {
    const messageData = newMessage.data
    if (!messageData) {
      throw new Error('newMessage.data is required for toolMessage updates')
    }

    const messageIndex = result.messages.findIndex(message => {
      return (
        message.type === MESSAGE_TYPE_TOOL_MESSAGE &&
        message.status === 'started' &&
        message.data &&
        message.data.parentRunId === messageData.parentRunId &&
        message.data.runId === messageData.runId
      )
    })

    if (messageIndex < 0) {
      return result
    }

    let value = newMessage.value
    try {
      value = JSON.parse(newMessage.value)
    } catch {
      console.warn('Failed to parse tool message value as JSON', newMessage.value)
    }

    const status = newMessage.type === MESSAGE_TYPE_TOOL_ERROR ? 'failed' : 'completed'
    return {
      ...result,
      messages: result.messages.map((message, index) =>
        index === messageIndex ? { ...message, ...newMessage, value, status, pendingApproval: false } : message
      ),
    }
  }

  if (
    result.messages.length > 0 &&
    result.messages.slice(-1)[0].type === MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM &&
    newMessage.type === MESSAGE_TYPE_INTERMEDIATE_ANSWER
  ) {
    return result
  }

  return {
    ...result,
    messages: insertRequestMessage(result.messages, newMessage),
  }
}

export const inferenceMessageReducer = (state: InferenceMessageState, action: InferenceMessageAction): InferenceMessageState => {
  switch (action.type) {
    case 'resetMessages':
      return initialInferenceMessageState

    case 'addMessage':
      return appendMessageInternal(state, action.message)

    case 'setMessages': {
      let newState: InferenceMessageState = { ...state, messages: [] }
      for (const message of action.messages) {
        newState = appendMessageInternal(newState, message)
      }
      return newState
    }

    case 'addStreamMessage':
      return {
        ...state,
        messages: insertRequestMessage(state.messages, tagBridgedChildMessage(state, action.message)),
      }

    case 'updateStreamMessage': {
      let newMessages = state.messages
      const existingStreamingMessageIndex = state.messages.findIndex(
        message =>
          (message.type === MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM || message.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM) &&
          (action.data?.requestMessageId
            ? (!message.requestMessageId || message.requestMessageId === action.data.requestMessageId) &&
              message.data?.parentRunId === action.data?.parentRunId &&
              (!message.data?.runId || !action.data.runId || message.data.runId === action.data.runId)
            : message.data?.parentRunId === action.data?.parentRunId)
      )
      if (
        !action.data?.requestMessageId &&
        existingStreamingMessageIndex < state.messages.length - 1 &&
        existingStreamingMessageIndex > -1
      ) {
        const existingStreamingMessage = state.messages[existingStreamingMessageIndex]
        newMessages = [
          ...state.messages.slice(0, existingStreamingMessageIndex),
          ...state.messages.slice(existingStreamingMessageIndex + 1),
          existingStreamingMessage,
        ]
      }

      const existingFinalAnswerStreamIndex = state.messages.findIndex(
        message =>
          (!action.data?.requestMessageId || !message.requestMessageId || message.requestMessageId === action.data.requestMessageId) &&
          message.data?.parentRunId !== action.data?.parentRunId &&
          message.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM &&
          message.value === ''
      )
      if (existingFinalAnswerStreamIndex > -1 && existingStreamingMessageIndex === -1 && action.data?.runId) {
        newMessages = [
          ...state.messages.slice(0, existingFinalAnswerStreamIndex),
          ...state.messages.slice(existingFinalAnswerStreamIndex + 1),
          {
            data: { runId: action.data.runId, parentRunId: action.data.parentRunId },
            requestMessageId: action.data.requestMessageId,
            type: MESSAGE_TYPE_FINAL_ANSWER_STREAM,
            value: '',
          },
        ]
      }

      if (newMessages.length === 0) {
        return state
      }

      const streamMessageIndex = action.data?.requestMessageId
        ? newMessages.findIndex(
            message =>
              (!message.requestMessageId || message.requestMessageId === action.data?.requestMessageId) &&
              message.data?.parentRunId === action.data?.parentRunId &&
              (!message.data?.runId || !action.data?.runId || message.data.runId === action.data.runId) &&
              (message.type === MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM || message.type === MESSAGE_TYPE_FINAL_ANSWER_STREAM)
          )
        : newMessages.length - 1
      if (streamMessageIndex === -1) return state
      const updatedMessages = newMessages.map((message, index) =>
        index === streamMessageIndex
          ? {
              ...message,
              value: `${message.value}${action.token}`,
              requestMessageId: action.data?.requestMessageId ?? message.requestMessageId,
            }
          : message
      )
      return {
        ...state,
        messages: action.data?.requestMessageId ? groupRequestMessages(updatedMessages, action.data.requestMessageId) : updatedMessages,
      }
    }

    case 'finalizeTemporaryMessage': {
      const streamMessageIndex = state.messages.findLastIndex(message => action.types.includes(message.type))
      if (streamMessageIndex > -1 && !state.messages[streamMessageIndex].id) {
        return {
          ...state,
          messages: state.messages.map((message, index) => (index === streamMessageIndex ? { ...message, id: action.id } : message)),
        }
      }
      console.warn(`No temporary message found to finalize for types ${action.types.join(', ')}`)
      return state
    }

    case 'updateQueuedMessage': {
      let messageIndex = state.messages.findIndex(message => message.id === action.update.messageId)
      if (messageIndex === -1) {
        messageIndex = state.messages.findIndex(message => message.type === 'userInput' && !message.id)
      }
      if (messageIndex === -1) {
        if (!action.update.predictionInput?.question) return state
        const newMessages = [
          ...state.messages,
          {
            id: action.update.messageId,
            type: 'userInput' as const,
            value: action.update.predictionInput.question,
            queueStatus: action.update.status,
            queueError: action.update.error,
            queuedFiles: action.update.files,
            chatFiles: action.update.files?.map(file => ({ id: file.chatFileId ?? '', fileName: file.fileName })),
          },
        ]
        return {
          ...state,
          messages: groupRequestMessages(newMessages, action.update.messageId),
        }
      }

      const updatedMessages = state.messages.map((message, index) => {
        if (index !== messageIndex) return message
        const updatedFiles = action.update.files?.length ? action.update.files : message.queuedFiles
        return {
          ...message,
          id: action.update.messageId,
          queueStatus: action.update.status,
          queueError: action.update.error,
          queuedFiles: updatedFiles,
          chatFiles: updatedFiles?.map(file => ({ id: file.chatFileId ?? '', fileName: file.fileName })) ?? message.chatFiles,
        }
      })
      return {
        ...state,
        messages: groupRequestMessages(updatedMessages, action.update.messageId),
      }
    }

    case 'updateTaskStatus': {
      const { taskId, info, status } = action.task
      if (!isExistingTask(action.task) || taskId === undefined) {
        return state
      }
      return {
        ...state,
        tasks: state.tasks.map(task => (task.taskId === taskId ? { ...task, status, info } : task)),
      }
    }

    case 'markToolCallPendingApproval':
      return {
        ...state,
        messages: state.messages.map(message => {
          const isMatchingToolCall =
            (action.messageUpdate.id &&
              (message.id === action.messageUpdate.id || message.toolCallMessageId === action.messageUpdate.id)) ||
            (action.messageUpdate.data?.runId && message.data?.runId === action.messageUpdate.data.runId)

          if (!isMatchingToolCall) {
            return message
          }

          return {
            ...message,
            pendingApproval: true,
            approval: { ...message.approval, status: 'pending' } as ToolCallApproval,
          }
        }),
      }

    case 'linkSubTeammateChildSession':
      return {
        ...state,
        messages: state.messages.map(message =>
          message.id === action.parentToolCallMessageId || message.toolCallMessageId === action.parentToolCallMessageId
            ? {
                ...message,
                subTeammateChildSessionId: action.childSessionId,
                subTeammateChildChatflowName: action.childChatflowName ?? message.subTeammateChildChatflowName,
              }
            : message
        ),
      }

    case 'setSubTeammateAcceptanceState':
      return {
        ...state,
        messages: state.messages.map(message =>
          message.id === action.parentToolCallMessageId || message.toolCallMessageId === action.parentToolCallMessageId
            ? {
                ...message,
                pendingUserAcceptance: action.pendingUserAcceptance,
                subTeammateStructuredOutput: action.structuredOutput,
              }
            : message
        ),
      }

    case 'clearSubTeammateAcceptanceState':
      return {
        ...state,
        messages: state.messages.map(message =>
          message.id === action.parentToolCallMessageId || message.toolCallMessageId === action.parentToolCallMessageId
            ? { ...message, pendingUserAcceptance: false }
            : message
        ),
      }

    case 'markSubTeammateRerunning':
      return {
        ...state,
        messages: state.messages.map(message =>
          message.id === action.parentToolCallMessageId || message.toolCallMessageId === action.parentToolCallMessageId
            ? { ...message, pendingUserAcceptance: false, subTeammateStructuredOutput: undefined }
            : message
        ),
      }
  }
}

export const createInferenceMessageActions = (dispatch: Dispatch<InferenceMessageAction>): InferenceMessageActions => ({
  resetMessages: () => dispatch({ type: 'resetMessages' }),
  addMessage: message => dispatch({ type: 'addMessage', message }),
  setMessages: messages => dispatch({ type: 'setMessages', messages }),
  addStreamMessage: message => dispatch({ type: 'addStreamMessage', message }),
  updateStreamMessage: ({ token, data }) => dispatch({ type: 'updateStreamMessage', token, data }),
  finalizeTemporaryMessage: (types, id) => dispatch({ type: 'finalizeTemporaryMessage', types, id }),
  updateQueuedMessage: update => dispatch({ type: 'updateQueuedMessage', update }),
  updateTaskStatus: task => dispatch({ type: 'updateTaskStatus', task }),
  markToolCallPendingApproval: messageUpdate => dispatch({ type: 'markToolCallPendingApproval', messageUpdate }),
  linkSubTeammateChildSession: (parentToolCallMessageId, childSessionId, childChatflowName) =>
    dispatch({ type: 'linkSubTeammateChildSession', parentToolCallMessageId, childSessionId, childChatflowName }),
  setSubTeammateAcceptanceState: ({ parentToolCallMessageId, pendingUserAcceptance, structuredOutput }) =>
    dispatch({ type: 'setSubTeammateAcceptanceState', parentToolCallMessageId, pendingUserAcceptance, structuredOutput }),
  clearSubTeammateAcceptanceState: parentToolCallMessageId =>
    dispatch({ type: 'clearSubTeammateAcceptanceState', parentToolCallMessageId }),
  markSubTeammateRerunning: parentToolCallMessageId => dispatch({ type: 'markSubTeammateRerunning', parentToolCallMessageId }),
})
